import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { communityApi } from 'community/api';
import { organizationApi } from 'organization/api';

import { permissionsApi } from './permissionsApi';

import type { RootState } from 'store';

export const permissionsApiListener = createListenerMiddleware<RootState>();

/**
 * The mutations that can change what the acting user may do, enumerated by name.
 *
 * A list rather than a broad matcher, because the alternative - invalidating on anything that looks
 * community-shaped - would refetch permissions on every branding edit and logo upload. Each entry
 * here changes a role, a membership, or which communities exist under an organisation:
 *
 * - **joining** and **registering to** a community, which is how a user acquires community-derived
 *   actions in the first place. `joinCommunityAsMember` deliberately does not invalidate the member
 *   tag today, because before this work there was no `/me` entry for that community to refresh.
 * - **community creation**, which has *two* endpoints - `communityApi` for the self-serve flow and
 *   `organizationApi` for the console - and missing either would leave the console's own creations
 *   answering from a stale snapshot.
 * - **community deletion and transfer**, which each remove a scope the snapshot may still be keyed
 *   on. Transfer hands the community to another organisation's owner, so it leaves the acting
 *   admin's reach exactly as deletion does - what matters is that the scope goes, not whether the
 *   community still exists.
 * - **promoting or demoting a community member**, via `toggleCommunityAdmin`.
 * - **editing a member's organisation role and community access**, via
 *   `updateOrganizationMemberAccess`. This is the only endpoint the frontend has for a member's
 *   access.
 *
 * Considered and left out: `removeCommunityMember`. It changes someone's access, but not the
 * *acting* user's own - and per the note below, the acting user is the only snapshot this can
 * refresh. `transferCommunity` was here too until review pointed out that it is deletion's twin,
 * not removal's.
 */
const INVALIDATING_MUTATIONS = isAnyOf(
  communityApi.endpoints.joinCommunityAsMember.matchFulfilled,
  communityApi.endpoints.registerToCommunity.matchFulfilled,
  communityApi.endpoints.createCommunity.matchFulfilled,
  organizationApi.endpoints.createCommunity.matchFulfilled,
  organizationApi.endpoints.deleteCommunity.matchFulfilled,
  organizationApi.endpoints.transferCommunity.matchFulfilled,
  communityApi.endpoints.toggleCommunityAdmin.matchFulfilled,
  organizationApi.endpoints.updateOrganizationMemberAccess.matchFulfilled
);

/**
 * Refresh the permission snapshot when one of those lands.
 *
 * **The whole tag type, not a per-scope id.** At most two entries are live at any moment - one
 * community scope and one organisation scope - so precision buys nothing, while a targeted tag can
 * miss: a community-level role change can move an organisation-scoped answer, the id is not in every
 * one of these mutations' arguments, and joining a community changes which scope resolves at all,
 * which opens a new entry rather than dirtying the old one.
 *
 * **This is client-local, and reaches exactly one person.** RTK Query invalidation runs in the
 * acting user's tab, so every mutation above refreshes the *acting* admin's snapshot and nobody
 * else's. There is no permission-invalidation websocket event to hook - the only community
 * subscription is the availability room - so a demoted user keeps their UI grants until they reload,
 * exactly as `getCurrentCommunityMember` behaves today. The cutover does not change that: the bound
 * is the SPA session, and the exposure is a control that 403s on click rather than an escalation,
 * because natter-backend still authorizes the write.
 */
permissionsApiListener.startListening({
  matcher: INVALIDATING_MUTATIONS,
  effect: (_action, { dispatch }) => {
    dispatch(permissionsApi.util.invalidateTags(['PERMISSIONS']));
  },
});
